import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, FileCheck2, Clock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/OrderBits";

export default function Dashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  const ready = (orders || []).filter((o) => o.status === "Results Ready").length;
  const active = (orders || []).filter((o) => o.status !== "Results Ready").length;

  return (
    <div className="bg-background" data-testid="dashboard-page">
      <div className="mx-auto max-w-6xl px-6 py-12 md:px-10 md:py-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-ocean">Welcome, {user?.username}</h1>
            <p className="mt-1 text-slate-600">Track your orders and view your results.</p>
          </div>
          <Link to="/pricing" data-testid="dashboard-new-order" className="inline-flex items-center gap-2 rounded-full bg-ocean px-6 py-3 text-sm font-semibold text-white hover:bg-ocean-light">
            <Plus className="h-4 w-4" /> New Test Order
          </Link>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Package, label: "Total orders", value: orders?.length ?? "—" },
            { icon: Clock, label: "In progress", value: active },
            { icon: FileCheck2, label: "Results ready", value: ready },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-border bg-white p-6">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-accent"><s.icon className="h-5 w-5 text-teal-dark" /></span>
              <p className="mt-4 text-2xl font-bold text-ocean">{s.value}</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-12 text-xl font-semibold text-ocean">Your orders</h2>
        <div className="mt-4 space-y-3">
          {orders === null && <div className="rounded-2xl border border-border bg-white p-10 text-center text-slate-400">Loading…</div>}
          {orders && orders.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border bg-white p-12 text-center" data-testid="dashboard-empty">
              <Package className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-4 font-semibold text-ocean">No orders yet</p>
              <p className="mt-1 text-sm text-slate-500">Order your first supplement test to get started.</p>
              <Link to="/pricing" className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal px-6 py-2.5 text-sm font-semibold text-white hover:bg-teal-dark">Choose a test</Link>
            </div>
          )}
          {orders && orders.map((o) => (
            <Link key={o.id} to={`/orders/${o.id}`} data-testid={`dashboard-order-${o.id}`}
              className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-white p-6 transition-shadow hover:shadow-[0_8px_30px_rgba(10,61,98,0.08)]">
              <div>
                <p className="font-semibold text-ocean">Order #{o.id.slice(0, 8).toUpperCase()}</p>
                <p className="mt-1 text-sm text-slate-500">{o.items.map((i) => i.tier_name).join(", ")} · {new Date(o.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-4">
                <StatusBadge status={o.status} />
                <span className="font-semibold text-ocean">${o.total?.toFixed(2)}</span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
